/**
 * Shared configuration reader for Vite-based web apps.
 *
 * All environment variables must use the VITE_ prefix.
 * Values are read at build time via import.meta.env.
 *
 * This module is designed to be called from app code that has
 * access to import.meta.env. The getEnv helper is a convenience
 * wrapper; apps may also read import.meta.env directly.
 */

export interface AppConfig {
  apiBaseUrl: string;
  requestSecurityEnabled: boolean;
  requestSecurityAnonymousSecret: string;
  captchaEnabled: boolean;
  captchaSceneId: string;
  captchaPrefix: string;
  captchaRegion: string;
}

function deriveApiBaseUrlFromLocation(): string {
  if (typeof globalThis === 'undefined' || !('location' in globalThis)) {
    return 'https://api.aiptyg.com';
  }
  const { protocol, hostname } = globalThis.location;
  const normalizedProtocol = protocol === 'http:' ? 'http:' : 'https:';
  const host = String(hostname || '').trim();
  if (host.startsWith('admin.')) {
    return `${normalizedProtocol}//api.${host.slice('admin.'.length)}`;
  }
  return 'https://api.aiptyg.com';
}

/**
 * Build an AppConfig from the current Vite environment.
 * Must be called from app code (not from a library at import time)
 * because import.meta.env is only available in Vite-processed modules.
 */
export function buildConfig(env: Record<string, string | boolean | undefined>): AppConfig {
  const apiBaseUrl = String(env.VITE_API_BASE_URL || '').trim() || deriveApiBaseUrlFromLocation();
  return {
    apiBaseUrl,
    requestSecurityEnabled: String(env.VITE_REQUEST_SECURITY_ENABLED) === 'true',
    requestSecurityAnonymousSecret: String(env.VITE_REQUEST_SECURITY_ANONYMOUS_SECRET || ''),
    captchaEnabled: String(env.VITE_CAPTCHA_ENABLED) === 'true',
    captchaSceneId: String(env.VITE_CAPTCHA_SCENE_ID || ''),
    captchaPrefix: String(env.VITE_CAPTCHA_PREFIX || ''),
    captchaRegion: String(env.VITE_CAPTCHA_REGION || ''),
  };
}
